import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Grid } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import moment from 'moment/moment';
import Modal from 'components/shared/Modal';
import AdaptiveTextQuestionFormItem from '../../AdaptiveTextQuestionFormItem';
import SelectQuestionFormItem from '../../SelectQuestionFormItem';
import DropdownCustomListQuestionFormItem from '../../DropdownCustomListQuestionFormItem';
import FileQuestionFormItem from '../../FileQuestionFormItem';
import DateQuestionFormItem from '../../DateQuestionFormItem';
import CurrencyQuestionFormItem from '../../CurrencyQuestionFormItem';
import PhoneQuestionFormItem from '../../PhoneQuestionFormItem';
import EmailQuestionFormItem from '../../EmailQuestionFormItem';
import { getISODateString } from 'util/Format';
import { getQuestionId, getSublistQuestionId } from 'util/Application';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { SublistQuestion } from 'store/types/ApplicationQuestion';
import { ApplicationFileAnswer, SublistAnswer } from 'store/types/ApplicationAnswer';
import { ModalProps } from 'store/types/ComponentProps';
import QuestionType from 'store/enums/QuestionType';
import FileInfo from 'store/types/FileInfo';
import { ApplicationQuestionOption } from 'store/types/ApplicationQuestion';
import countries from 'store/configs/Countries';

import commonStyles from 'styles/common.module.scss';

interface AddSublistItemModalProps extends ModalProps {
  questions: SublistQuestion[];
  fieldName: string;
  parentQuestionType?: QuestionType;
}

const countryOptions: ApplicationQuestionOption[] = countries.map(({ value, text }) => ({
  value,
  name: text,
}));

const getQuestionInput = (
  fieldName: string,
  referenceKey: string,
  { type, options, required, fileRequired, files }: SublistQuestion,
  open: boolean
) => {
  const inputProps = {
    fieldName,
    options,
    disabled: !open,
    required: required && open,
    fileRequired: fileRequired && open,
    files,
  };

  switch (type) {
    case QuestionType.Text:
      return <AdaptiveTextQuestionFormItem {...inputProps} />;
    case QuestionType.Currency:
      return <CurrencyQuestionFormItem {...inputProps} />;
    case QuestionType.Date:
      return <DateQuestionFormItem {...inputProps} />;
    case QuestionType.Dropdown:
      return <SelectQuestionFormItem {...inputProps} />;
    case QuestionType.DropdownCustomList:
      return <DropdownCustomListQuestionFormItem {...inputProps} />;
    case QuestionType.File:
      return <FileQuestionFormItem key={referenceKey} {...inputProps} showTable={false} />;
    case QuestionType.Checkbox:
      return <></>;
    case QuestionType.Phone:
      return <PhoneQuestionFormItem {...inputProps} />;
    case QuestionType.Email:
      return <EmailQuestionFormItem {...inputProps} />;
    case QuestionType.Country:
      return <SelectQuestionFormItem {...inputProps} options={countryOptions} />;
    default:
      return <></>;
  }
};

const getQuestionFieldName = (questionId: string): string => `sublistQuestion-${questionId}`;

const getDefaultViewValues = (questions: SublistQuestion[]) => {
  return questions.reduce((acc, { id, type }) => {
    const baseName = getQuestionFieldName(id);

    acc[baseName] = '';

    if (type === QuestionType.Currency) {
      acc[`${baseName}-type`] = '';
      acc[`${baseName}-amount`] = '';
    }

    return acc;
  }, {} as Record<string, any>);
};

const AddSublistItemModal: React.FunctionComponent<AddSublistItemModalProps> = ({
  onClose,
  open,
  questions,
  fieldName,
}) => {
  const { control, setValue, reset, getValues, trigger, getFieldState, formState } = useFormContext();
  const [referenceKey, setReferenceKey] = useState<string>(uuid());
  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const controlFieldNameView = useMemo(() => `${fieldName}-view`, [fieldName]);
  const controlFieldNameData = useMemo(() => `${fieldName}-data`, [fieldName]);
  const sublistAnswers: Array<SublistAnswer[]> = useWatch({ control, name: fieldName });
  const sublistAnswersView = useWatch({ control, name: controlFieldNameView });

  const getControlName = useCallback(
    (questionId: string) => `${controlFieldNameView}.${getQuestionFieldName(questionId)}`,
    [controlFieldNameView]
  );

  const sublistFieldNames = useMemo(() => questions.map(({ id }) => getControlName(id)), [questions, getControlName]);

  const addButtonDisabled: boolean = useMemo(() => {
    if (!sublistAnswersView) return true;

    return questions.some(({ id }) => {
      const fieldName = getControlName(id);
      const { error } = getFieldState(fieldName, formState);
      return !!error;
    });
  }, [questions, sublistAnswersView, getControlName, getFieldState, formState]);

  const generateNewReferenceKey = useCallback(() => {
    setReferenceKey(uuid());
  }, []);

  const scrollToTop = useCallback(() => {
    requestAnimationFrame(() => {
      modalContentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const handleAddItem = useCallback(
    async (close?: boolean) => {
      const isValid = await trigger(sublistFieldNames, { shouldFocus: true });

      if (!isValid) {
        setShowValidationError(true);
        scrollToTop();
        return;
      }

      setShowValidationError(false);

      const values = getValues();
      const files = values['files'] || {};
      const existingData = Object.values(values[controlFieldNameData] ?? []);

      const newAnswers = [...existingData];

      const sublistFiles: ApplicationFileAnswer = files ? files[controlFieldNameView] : {};

      Object.entries(sublistFiles || {}).forEach(([key, fileArray]) => {
        if (!fileArray?.length) return;

        const questionId = getQuestionId(fieldName);
        const sublistQuestionId = getSublistQuestionId(key);
        const question = questions.find(({ id }) => id === sublistQuestionId);

        newAnswers.push({
          answer: 'File',
          type: question?.type || QuestionType.File,
          content: question?.content || '',
          sublistQuestionId,
          files: fileArray.map((f: FileInfo) => ({ name: f.name })),
          referenceKey,
        });

        setValue(`sublistFiles.${referenceKey}-${sublistQuestionId}`, {
          referenceKey,
          questionId,
          sublistQuestionId,
          files: fileArray || [],
        });

        setValue(`files.${getControlName(sublistQuestionId)}`, []);
      });

      setValue(fieldName, [...(sublistAnswers || []), newAnswers]);
      setValue(controlFieldNameView, getDefaultViewValues(questions));
      setValue(controlFieldNameData, '');

      generateNewReferenceKey();

      if (close) {
        onClose();
      }
    },
    [
      trigger,
      sublistFieldNames,
      getValues,
      controlFieldNameData,
      controlFieldNameView,
      setValue,
      fieldName,
      sublistAnswers,
      questions,
      generateNewReferenceKey,
      scrollToTop,
      referenceKey,
      getControlName,
      onClose,
    ]
  );

  const handleClose = useCallback(() => {
    setShowValidationError(false);

    reset(
      {
        ...getValues(),
        [controlFieldNameView]: '',
        [controlFieldNameData]: '',
      },
      { keepDirty: true }
    );
    onClose();
  }, [controlFieldNameData, controlFieldNameView, getValues, onClose, reset]);

  useEffect(() => {
    if (open) {
      setShowValidationError(false);

      reset({
        ...getValues(),
        [controlFieldNameView]: getDefaultViewValues(questions),
      });
    }
  }, [controlFieldNameData, controlFieldNameView, getValues, open, questions, reset]);

  useEffect(() => {
    if (!sublistAnswersView) return;

    questions.forEach(({ id, type, content }) => {
      const questionValue = sublistAnswersView[getQuestionFieldName(id)];
      if (questionValue) {
        setValue(`${controlFieldNameData}.${id}`, {
          answer: moment.isMoment(questionValue) ? getISODateString(questionValue) || '' : questionValue,
          sublistQuestionId: id,
          type,
          content,
          referenceKey,
        });
      }
    });
  }, [controlFieldNameData, controlFieldNameView, questions, referenceKey, setValue, sublistAnswersView]);

  return (
    <Modal
      onClose={handleClose}
      open={open}
      title={'Add New Item'}
      actions={
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps} xs={true}>
            <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
              {'Cancel'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button
              color={'secondary'}
              variant={'contained'}
              onClick={() => handleAddItem(false)}
              disabled={addButtonDisabled}
            >
              {'Add & Create New'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button
              color={'secondary'}
              variant={'contained'}
              onClick={() => handleAddItem(true)}
              disabled={addButtonDisabled}
            >
              {'Add'}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <div ref={modalContentRef} />
      <Alert severity={'error'} className={commonStyles.alert} hidden={!showValidationError}>
        All required fields must be completed before adding this item.
      </Alert>

      <Grid {...defaultGridContainerProps}>
        {questions.map((question) => (
          <Grid {...defaultGridItemProps} key={question.id}>
            <h5 className={commonStyles.subTitle}>
              {question.content}
              {question.required ? '*' : ''}
            </h5>
            {getQuestionInput(getControlName(question.id), referenceKey, question, open)}
          </Grid>
        ))}
      </Grid>
    </Modal>
  );
};
export default AddSublistItemModal;
